import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import UpgradeGate from '../components/UpgradeGate';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Bell, CalendarPlus, RefreshCw, XCircle, UserX, ThumbsUp, Mail, MessageSquare, ChevronLeft, Eye, BarChart3, FileText, Send } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS_EN = { reminders: 'Reminders', appointment_updates: 'Appointment updates' };
const ICONS = { reminder_24h: Bell, reminder_1h: Bell, new_appointment: CalendarPlus, rescheduled: RefreshCw, cancelled: XCircle, no_show: UserX, thank_you: ThumbsUp };

export default function MarketingPage() {
  const { t } = useTranslation();
  const TABS = [
    { key: 'campaigns', label: t('marketing.campaigns') },
    { key: 'automations', label: t('marketing.automations') },
    { key: 'messages', label: t('marketing.messages') },
    { key: 'templates', label: t('marketing.templates') },
  ];
  const CATEGORY_LABELS = { reminders: t('marketing.automations'), appointment_updates: t('marketing.automations') };
  const [tab, setTab] = useState('campaigns');
  const [automations, setAutomations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msgTotal, setMsgTotal] = useState(0);
  const [msgPage, setMsgPage] = useState(1);
  const [detailAuto, setDetailAuto] = useState(null);
  const [detailTab, setDetailTab] = useState('performance');
  const [autoStats, setAutoStats] = useState(null);
  const [autoPreview, setAutoPreview] = useState(null);
  const [viewMsg, setViewMsg] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignTemplates, setCampaignTemplates] = useState({});
  const [audienceFilters, setAudienceFilters] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [campForm, setCampForm] = useState({ name: '', subject: '', body_html: '', template_key: 'custom', audience: 'all' });
  const [audienceCount, setAudienceCount] = useState(null);
  const [sending, setSending] = useState(false);

  const fetchAutomations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/marketing/automations`, { withCredentials: true });
      setAutomations(data);
    } catch { toast.error('Failed to load automations'); }
  }, []);

  const fetchMessages = useCallback(async (p = 1) => {
    try {
      const { data } = await axios.get(`${API}/marketing/messages?page=${p}&limit=30`, { withCredentials: true });
      setMessages(data.messages);
      setMsgTotal(data.total);
      setMsgPage(data.page);
    } catch { toast.error('Failed to load messages'); }
  }, []);

  useEffect(() => { fetchAutomations(); }, [fetchAutomations]);
  useEffect(() => { if (tab === 'messages') fetchMessages(); }, [tab, fetchMessages]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const [campRes, tplRes] = await Promise.all([
        axios.get(`${API}/marketing/campaigns`, { withCredentials: true }),
        axios.get(`${API}/marketing/campaign-templates`, { withCredentials: true }),
      ]);
      setCampaigns(campRes.data);
      setCampaignTemplates(tplRes.data.templates || {});
      setAudienceFilters(tplRes.data.audiences || {});
    } catch {}
  }, []);

  useEffect(() => { if (tab === 'campaigns') fetchCampaigns(); }, [tab, fetchCampaigns]);

  const createCampaign = async (andSend = false) => {
    try {
      const { data } = await axios.post(`${API}/marketing/campaigns`, { ...campForm, status: 'draft' }, { withCredentials: true });
      if (andSend) {
        setSending(true);
        const res = await axios.post(`${API}/marketing/campaigns/${data.id}/send`, {}, { withCredentials: true });
        toast.success(`Sending to ${res.data.count} clients`);
        setSending(false);
      } else {
        toast.success('Campaign saved as draft');
      }
      setCreateOpen(false);
      setCampForm({ name: '', subject: '', body_html: '', template_key: 'custom', audience: 'all' });
      fetchCampaigns();
    } catch (err) {
      setSending(false);
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const sendCampaign = async (cid) => {
    if (!window.confirm('Send this campaign to all matching clients?')) return;
    try {
      setSending(true);
      const { data } = await axios.post(`${API}/marketing/campaigns/${cid}/send`, {}, { withCredentials: true });
      toast.success(`Sending to ${data.count} clients`);
      setSending(false);
      fetchCampaigns();
    } catch (err) {
      setSending(false);
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const deleteCampaign = async (cid) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await axios.delete(`${API}/marketing/campaigns/${cid}`, { withCredentials: true });
      toast.success('Deleted');
      fetchCampaigns();
    } catch { toast.error('Failed'); }
  };

  const applyTemplate = (key) => {
    const tpl = campaignTemplates[key];
    if (tpl) {
      setCampForm(p => ({ ...p, template_key: key, name: tpl.name, subject: tpl.subject, body_html: tpl.body }));
    }
  };

  const toggleAuto = async (key, currentEnabled) => {
    try {
      await axios.put(`${API}/marketing/automations/${key}`, { enabled: !currentEnabled }, { withCredentials: true });
      setAutomations(prev => prev.map(a => a.key === key ? { ...a, enabled: !currentEnabled } : a));
      toast.success(!currentEnabled ? 'Automation enabled' : 'Automation disabled');
    } catch { toast.error('Failed'); }
  };

  const openDetail = async (auto) => {
    setDetailAuto(auto);
    setDetailTab('performance');
    setAutoStats(null);
    setAutoPreview(null);
    try {
      const { data } = await axios.get(`${API}/marketing/automations/${auto.key}/stats`, { withCredentials: true });
      setAutoStats(data);
    } catch {}
  };

  const loadPreview = async (key) => {
    try {
      const { data } = await axios.get(`${API}/marketing/automations/${key}/preview`, { withCredentials: true });
      setAutoPreview(data);
    } catch {}
  };

  const loadMsgDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API}/marketing/messages/${id}`, { withCredentials: true });
      setViewMsg(data);
    } catch { toast.error('Failed'); }
  };

  const grouped = {};
  automations.forEach(a => {
    if (!grouped[a.category]) grouped[a.category] = [];
    grouped[a.category].push(a);
  });

  const typeLabels = { confirmation: 'Confirmation', reminder: 'Reminder', reschedule: 'Rescheduled', cancellation: 'Cancellation', no_show: 'No-Show', thank_you: 'Thank You' };
  const statusColors = { sent: 'bg-[#3E6641] text-white', error: 'bg-[#E2725B] text-white', pending: 'bg-[#D4A373] text-white' };

  if (detailAuto) {
    return (
      <div className="fade-in space-y-6" data-testid="automation-detail">
        <button onClick={() => setDetailAuto(null)} className="flex items-center gap-1 text-sm text-[#556B2F] hover:underline" data-testid="back-to-automations">
          <ChevronLeft size={14} /> Back to Automations
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1A2421] flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
              {detailAuto.name}
              <Badge className={`text-xs ${detailAuto.enabled ? 'bg-[#3E6641] text-white' : 'bg-[#E5E7E1] text-[#5C6656]'}`}>
                {detailAuto.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </h1>
            <p className="text-sm text-[#5C6656] mt-1">{detailAuto.description}</p>
          </div>
          <Button variant="outline" className="border-[#E5E7E1]" onClick={() => toggleAuto(detailAuto.key, detailAuto.enabled)} data-testid="toggle-detail-btn">
            {detailAuto.enabled ? 'Disable' : 'Enable'}
          </Button>
        </div>

        <div className="flex gap-1">
          {['performance', 'preview'].map(tabKey => (
            <button key={tabKey} onClick={() => { setDetailTab(tabKey); if (tabKey === 'preview' && !autoPreview) loadPreview(detailAuto.key); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${detailTab === tabKey ? 'bg-[#1A2421] text-white' : 'text-[#5C6656] hover:bg-[#F5F5F3]'}`}
              data-testid={`detail-tab-${tabKey}`}>
              {tabKey.charAt(0).toUpperCase() + tabKey.slice(1)}
            </button>
          ))}
        </div>

        {detailTab === 'performance' && (
          <div className="space-y-4">
            {autoStats?.chart_data?.length > 0 && (
              <Card className="border-[#E5E7E1]">
                <CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Sent messages over time</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={autoStats.chart_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7E1" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#5C6656' }} tickFormatter={d => d.slice(5)} />
                      <YAxis tick={{ fontSize: 10, fill: '#5C6656' }} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7E1', fontSize: 12 }} />
                      <Bar dataKey="email" fill="#556B2F" radius={[3,3,0,0]} name="Email" />
                      <Bar dataKey="sms" fill="#D4A373" radius={[3,3,0,0]} name="SMS" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex gap-4 mt-2 text-xs text-[#5C6656]">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#556B2F]" />Email</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#D4A373]" />SMS</span>
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Sent', value: autoStats?.total_sent || 0, sub: `Email: ${autoStats?.total_email || 0}` },
                { label: 'Delivered', value: autoStats?.total_sent || 0, sub: `Email: ${autoStats?.total_email || 0} (100%)` },
                { label: 'Email', value: autoStats?.total_email || 0, sub: 'Total email messages' },
                { label: 'SMS', value: autoStats?.total_sms || 0, sub: 'Total SMS messages' },
              ].map((s, i) => (
                <Card key={i} className="border-[#E5E7E1]"><CardContent className="p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#5C6656]">{s.label}</p>
                  <p className="text-2xl font-bold text-[#1A2421] mt-1">{s.value}</p>
                  <p className="text-[11px] text-[#5C6656] mt-0.5">{s.sub}</p>
                </CardContent></Card>
              ))}
            </div>
            {(!autoStats || autoStats.total_sent === 0) && (
              <div className="text-center py-12 text-[#5C6656]">
                <BarChart3 size={40} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">No messages sent yet</p>
                <p className="text-xs mt-1">Messages will appear here once this automation triggers</p>
              </div>
            )}
          </div>
        )}

        {detailTab === 'preview' && (
          <div className="space-y-4">
            {autoPreview?.html && (
              <Card className="border-[#E5E7E1]">
                <CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Email Preview</CardTitle></CardHeader>
                <CardContent>
                  <div className="border border-[#E5E7E1] rounded-lg overflow-hidden bg-[#F5F5F3]">
                    <iframe srcDoc={autoPreview.html} title="Email Preview" className="w-full border-0" style={{ height: 600 }} sandbox="allow-same-origin" data-testid="email-preview-iframe" />
                  </div>
                </CardContent>
              </Card>
            )}
            {autoPreview?.sms && (
              <Card className="border-[#E5E7E1]">
                <CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>SMS Preview</CardTitle></CardHeader>
                <CardContent>
                  <div className="p-4 bg-[#F9F8F6] rounded-lg border border-[#E5E7E1]">
                    <p className="text-sm text-[#1A2421]">{autoPreview.sms}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <UpgradeGate feature="marketing" featureName="Marketing">
    <div className="fade-in space-y-6" data-testid="marketing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>{t('marketing.title')}</h1>
          <p className="text-sm text-[#5C6656] mt-1">{t('marketing.subtitle')}</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#E5E7E1] pb-0 overflow-x-auto">
        {[{ key: 'campaigns', label: t('marketing.campaigns'), icon: Send }, { key: 'automations', label: t('marketing.automations'), icon: Bell }, { key: 'messages', label: t('marketing.messages'), icon: Mail }].map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-[1px] ${tab === tb.key ? 'border-[#556B2F] text-[#1A2421]' : 'border-transparent text-[#5C6656] hover:text-[#1A2421]'}`}
            data-testid={`marketing-tab-${tb.key}`}>
            <tb.icon size={15} /> {tb.label}
          </button>
        ))}
      </div>

      {/* CAMPAIGNS TAB */}
      {tab === 'campaigns' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('marketing.emailCampaigns')}</h2>
              <p className="text-xs text-[#5C6656]">{t('marketing.emailCampaignsDesc')}</p>
            </div>
            <Button onClick={() => { setCreateOpen(true); setCampForm({ name: '', subject: '', body_html: '', template_key: 'custom', audience: 'all' }); }} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white h-9 text-sm gap-1.5" data-testid="create-campaign-btn">
              <Send size={14} /> {t('marketing.newCampaign')}
            </Button>
          </div>

          {campaigns.length === 0 ? (
            <Card className="border-[#E5E7E1]"><CardContent className="py-16 text-center">
              <Mail size={40} className="mx-auto mb-3 text-[#E5E7E1]" />
              <p className="text-sm font-medium text-[#1A2421]">{t('marketing.noCampaignsYet')}</p>
              <p className="text-xs text-[#5C6656] mt-1">{t('marketing.createFirstCampaign')}</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {campaigns.map(c => (
                <Card key={c.id} className="border-[#E5E7E1]" data-testid={`campaign-${c.id}`}>
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${c.status === 'sent' ? 'bg-[#3E6641]/10' : c.status === 'sending' ? 'bg-[#D4A373]/10' : 'bg-[#E5E7E1]'}`}>
                      {c.status === 'sent' ? <Mail size={18} className="text-[#3E6641]" /> : c.status === 'sending' ? <RefreshCw size={18} className="text-[#D4A373] animate-spin" /> : <FileText size={18} className="text-[#5C6656]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1A2421]">{c.name}</p>
                      <p className="text-xs text-[#5C6656] truncate">{c.subject}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <Badge className={`text-[10px] capitalize ${c.status === 'sent' ? 'bg-[#3E6641] text-white' : c.status === 'sending' ? 'bg-[#D4A373] text-white' : 'bg-[#E5E7E1] text-[#5C6656]'}`}>{c.status}</Badge>
                      {c.status === 'sent' && <p className="text-xs text-[#5C6656] mt-1">{c.sent_count} sent{c.failed_count > 0 ? `, ${c.failed_count} failed` : ''}</p>}
                      {c.sent_at && <p className="text-[10px] text-[#5C6656]">{new Date(c.sent_at).toLocaleDateString('en-GB', {day:'numeric', month:'short', hour:'2-digit', minute:'2-digit'})}</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {c.status === 'draft' && <Button size="sm" onClick={() => sendCampaign(c.id)} disabled={sending} className="bg-[#556B2F] text-white h-7 text-xs px-2">Send</Button>}
                      <Button size="sm" variant="ghost" onClick={() => deleteCampaign(c.id)} className="text-[#E2725B] h-7 text-xs px-2">Delete</Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Create Campaign Dialog */}
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogContent className="sm:max-w-2xl border-[#E5E7E1] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Create Campaign</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <p className="text-xs font-bold text-[#5C6656] uppercase tracking-wider mb-2">Start from a template</p>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(campaignTemplates).filter(([k]) => k !== 'custom').map(([key, tpl]) => (
                      <button key={key} type="button" onClick={() => applyTemplate(key)}
                        className={`p-3 rounded-xl border text-left transition-colors ${campForm.template_key === key ? 'border-[#556B2F] bg-[#F5F7F0]' : 'border-[#E5E7E1] hover:border-[#556B2F]/50'}`}
                        data-testid={`template-${key}`}>
                        <p className="text-sm font-medium text-[#1A2421]">{tpl.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-[#5C6656] uppercase">Campaign name</label>
                    <input value={campForm.name} onChange={e => setCampForm(p => ({ ...p, name: e.target.value }))} className="w-full h-9 px-3 text-sm border border-[#E5E7E1] rounded-lg" placeholder="e.g. Summer Special" data-testid="campaign-name" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-[#5C6656] uppercase">Audience</label>
                    <select value={campForm.audience} onChange={e => setCampForm(p => ({ ...p, audience: e.target.value }))} className="w-full h-9 px-3 text-sm border border-[#E5E7E1] rounded-lg bg-white" data-testid="campaign-audience">
                      {Object.entries(audienceFilters).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#5C6656] uppercase">Email subject</label>
                  <input value={campForm.subject} onChange={e => setCampForm(p => ({ ...p, subject: e.target.value }))} className="w-full h-9 px-3 text-sm border border-[#E5E7E1] rounded-lg" placeholder="e.g. We miss you at {business_name}!" data-testid="campaign-subject" />
                  <p className="text-[10px] text-[#5C6656]">Use {'{client_name}'} and {'{business_name}'} for personalization</p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-[#5C6656] uppercase">Email body</label>
                  <textarea value={campForm.body_html} onChange={e => setCampForm(p => ({ ...p, body_html: e.target.value }))} rows={6} className="w-full px-3 py-2 text-sm border border-[#E5E7E1] rounded-lg resize-none" placeholder="Write your message... HTML supported. Use {client_name} and {business_name}" data-testid="campaign-body" />
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[#E5E7E1]">
                  <Button variant="outline" onClick={() => createCampaign(false)} className="border-[#E5E7E1] text-[#5C6656] h-9 text-sm" data-testid="save-draft-btn">Save as Draft</Button>
                  <Button onClick={() => createCampaign(true)} disabled={sending || !campForm.subject || !campForm.body_html} className="bg-[#556B2F] hover:bg-[#4A5D23] text-white h-9 text-sm gap-1.5" data-testid="send-campaign-btn">
                    <Send size={14} /> {sending ? 'Sending...' : 'Send Now'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {tab === 'automations' && (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h2 className="text-lg font-semibold text-[#1A2421] mb-4" style={{ fontFamily: 'Outfit' }}>{CATEGORY_LABELS[cat] || cat}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {items.map(auto => {
                  const Icon = ICONS[auto.key] || Mail;
                  return (
                    <Card key={auto.key} className="border-[#E5E7E1] hover:border-[#556B2F]/30 transition-colors cursor-pointer group" data-testid={`auto-card-${auto.key}`}>
                      <CardContent className="p-5" onClick={() => openDetail(auto)}>
                        <Icon size={24} className="text-[#556B2F] mb-3" />
                        <h3 className="text-sm font-semibold text-[#1A2421] mb-1 group-hover:text-[#556B2F] transition-colors">{auto.name}</h3>
                        <p className="text-xs text-[#5C6656] mb-4 line-clamp-2">{auto.description}</p>
                        <div className="flex items-center justify-between">
                          <button onClick={(e) => { e.stopPropagation(); toggleAuto(auto.key, auto.enabled); }}
                            className={`px-3 py-1 rounded text-xs font-semibold border transition-colors ${auto.enabled ? 'border-[#3E6641] text-[#3E6641] hover:bg-[#3E6641] hover:text-white' : 'border-[#E5E7E1] text-[#5C6656] hover:border-[#556B2F] hover:text-[#556B2F]'}`}
                            data-testid={`toggle-${auto.key}`}>
                            {auto.enabled ? 'Enabled' : 'Disabled'}
                          </button>
                          <Mail size={14} className="text-[#5C6656]" />
                        </div>
                        {auto.stats?.sent > 0 && (
                          <p className="text-[10px] text-[#5C6656] mt-2">{auto.stats.sent} sent</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'messages' && (
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-16 text-[#5C6656]">
              <Mail size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">No messages sent yet</p>
              <p className="text-xs mt-1">Messages will appear here when automations trigger</p>
            </div>
          ) : (
            <Card className="border-[#E5E7E1]">
              <CardContent className="p-0">
                <div className="max-h-[600px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white border-b border-[#E5E7E1]">
                      <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-[#5C6656]">
                        <th className="px-5 py-3">Date</th>
                        <th className="px-3 py-3">Client</th>
                        <th className="px-3 py-3">Type</th>
                        <th className="px-3 py-3">Channel</th>
                        <th className="px-3 py-3">Status</th>
                        <th className="px-3 py-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map(m => (
                        <tr key={m.id} className="border-b border-[#E5E7E1] last:border-0 hover:bg-[#F9F8F6] transition-colors" data-testid={`msg-row-${m.id}`}>
                          <td className="px-5 py-3 text-[#5C6656] text-xs">{new Date(m.sent_at).toLocaleString()}</td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-[#1A2421]">{m.client_name}</p>
                            <p className="text-[11px] text-[#5C6656]">{m.client_email}</p>
                          </td>
                          <td className="px-3 py-3">
                            <Badge className="bg-[#E9EDDF] text-[#556B2F] text-[10px]">{typeLabels[m.template_type] || m.template_type}</Badge>
                          </td>
                          <td className="px-3 py-3">
                            <span className="flex items-center gap-1 text-xs text-[#5C6656]">
                              {m.channel === 'email' ? <Mail size={12} /> : <MessageSquare size={12} />}
                              {m.channel === 'email' ? 'Email' : 'SMS'}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <Badge className={`text-[10px] capitalize ${statusColors[m.status] || 'bg-[#E5E7E1] text-[#5C6656]'}`}>{m.status}</Badge>
                          </td>
                          <td className="px-3 py-3 text-right">
                            {m.channel === 'email' && (
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-[#556B2F]" onClick={() => loadMsgDetail(m.id)} data-testid={`view-msg-${m.id}`}>
                                <Eye size={13} className="mr-1" /> View
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {msgTotal > 30 && (
                  <div className="flex items-center justify-center gap-2 p-3 border-t border-[#E5E7E1]">
                    <Button variant="outline" size="sm" disabled={msgPage <= 1} onClick={() => fetchMessages(msgPage - 1)}>Prev</Button>
                    <span className="text-xs text-[#5C6656]">Page {msgPage}</span>
                    <Button variant="outline" size="sm" onClick={() => fetchMessages(msgPage + 1)}>Next</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Message Preview Dialog */}
      <Dialog open={!!viewMsg} onOpenChange={() => setViewMsg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>View message</DialogTitle>
          </DialogHeader>
          {viewMsg && (
            <div className="space-y-3">
              <p className="text-sm text-[#5C6656]">{viewMsg.subject}</p>
              <div className="border border-[#E5E7E1] rounded-lg overflow-hidden bg-[#F5F5F3]">
                <iframe srcDoc={viewMsg.email_html} title="Email" className="w-full border-0" style={{ height: 500 }} sandbox="allow-same-origin" data-testid="msg-preview-iframe" />
              </div>
              <div className="flex items-center gap-4 text-xs text-[#5C6656]">
                <span>To: {viewMsg.client_email}</span>
                <span>Sent: {new Date(viewMsg.sent_at).toLocaleString()}</span>
                <Badge className={`text-[10px] ${statusColors[viewMsg.status]}`}>{viewMsg.status}</Badge>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </UpgradeGate>
  );
}